import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { useRouteMatch } from 'react-router';

import { ChevronLeftIcon } from '@treams/treams-icons';
import {
	Container,
	Button,
	Toolbar,
	Title,
	Author,
	TextBubble,
	Subheading,
	Heading,
	Skeleton,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import { CompetencyDisplayWithValue } from 'packages/kakapo-ui-application/pages/evaluation-submissions/display/CompetencyDisplay';
import VoiceMemoPlayback from 'voice-memos/VoiceMemoPlayback';
import GoalCard from '../goals/GoalCard';
import { CommentsWithData } from 'comments/Comments';
import CommentBar from 'comments/components/CommentBar';

function OutgoingFeedbackAnswer(props: {
	recipient: graphql.User;
	response: graphql.FeedbackResponse;
	question: graphql.FeedbackRequestQuestion;
	answer: graphql.FeedbackResponseAnswer;
}) {
	const [composer, setComposer] = useState(false);
	const metadata = { questionID: props.question.id };

	return (
		<Box mt={4}>
			<Subheading mb={2}>{props.question.text}</Subheading>
			<Box mb={3}>
				{props.question.goal && (
					<GoalCard small goal={props.question.goal as unknown as graphql.Goal} />
				)}
			</Box>

			<Box mb={3}>
				<CommentBar.Container
					onComment={() => setComposer((composer) => !composer)}
					targetID={props.response.id}
					targetType={graphql.CommentTargetType.FeedbackResponse}
					metadata={metadata}
				>
					<TextBubble
						user={props.recipient}
						text={props.answer.text}
						stars={props.answer.stars ?? undefined}
					>
						{props.question.competency && typeof props.answer.competencyLevelIndex === 'number' && (
							<Box mt={2}>
								<CompetencyDisplayWithValue
									levels={props.question.competency.levels}
									manager={props.answer.competencyLevelIndex}
								/>
							</Box>
						)}
						{props.answer.voiceMemo && (
							<Box py={3}>
								<VoiceMemoPlayback
									samples={props.answer.voiceMemo.samples}
									source={props.answer.voiceMemo.asset.source.url}
								/>
							</Box>
						)}
					</TextBubble>
				</CommentBar.Container>
			</Box>

			<Box ml={4}>
				<CommentsWithData
					empty={false}
					composer={composer}
					target={props.response}
					comments={props.response.comments}
					autoFocus
					metadata={metadata}
					onRefetch={() => {}}
					onCancelComposer={() => setComposer(false)}
				/>
			</Box>
		</Box>
	);
}

export default function OutgoingFeedback() {
	const { t } = useTranslation();

	const { params } = useRouteMatch<{ userID?: string; id: string }>();
	const { userID, id } = params;

	const query = graphql.useInternalFeedbackQuery({ variables: { id } });

	return (
		<Container py={4}>
			<Toolbar>
				<Button
					icon={<ChevronLeftIcon />}
					link={userID ? `/users/${userID}/feedback/given` : '/feedback/given'}
					title={t('goals.show.toolbar.back')}
					type="secondary"
				/>
			</Toolbar>

			<Box mt={4}>
				{query.data ? (
					<>
						<Title mb={1}>
							{t('feedback.outgoing.detail.title', {
								name: query.data.internalFeedback.requestor.firstName,
							})}
						</Title>
						<Heading mb={3}>{query.data.internalFeedback.title}</Heading>
						<Author
							user={query.data.internalFeedback.requestor as graphql.User}
							description={t('feedback.outgoing.detail.createdAt')}
							createdAt={query.data.internalFeedback.sharedAt}
						/>

						<Box width={[1, 1, 9 / 12]}>
							{query.data.internalFeedback.answers?.map((answer, index) => {
								// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
								const question = query.data?.internalFeedback.questions[index]!;

								return (
									<OutgoingFeedbackAnswer
										key={answer.questionID}
										response={query.data?.internalFeedback as graphql.FeedbackResponse}
										answer={answer as graphql.FeedbackResponseAnswer}
										question={question as graphql.FeedbackRequestQuestion}
										recipient={query.data?.internalFeedback.recipient as graphql.User}
									/>
								);
							})}
						</Box>
					</>
				) : (
					<>
						<Title.Skeleton mb={1} />
						<Heading.Skeleton mb={3} />
						<Author.Skeleton />

						<Box width={[1, 1, 9 / 12]}>
							<Skeleton.Repeat count={3}>
								<Box mt={4}>
									<Subheading.Skeleton mb={2} />
									<TextBubble.Skeleton />
								</Box>
							</Skeleton.Repeat>
						</Box>
					</>
				)}
			</Box>
		</Container>
	);
}
